import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { toast } from "react-toastify";
import { apiService } from "../../../services/api";
import { Funnel } from "../../../types/funnels";
import Select from "react-select";

const route = all_routes;

const ConfiguracoesOrcamento = () => {
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [selectedFunnel, setSelectedFunnel] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'etapas' | 'valor'>('etapas');
  
  // Estados para configurações de etapas
  const [etapasConfig, setEtapasConfig] = useState<any[]>([]);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  
  // Estados para configuração de valor
  const [valorConfig, setValorConfig] = useState<any | null>(null);
  const [loadingValor, setLoadingValor] = useState(false);
  const [etapaMudancaValor, setEtapaMudancaValor] = useState<number | null>(null);
  
  // Carregar funis disponíveis
  useEffect(() => {
    const loadFunnels = async () => {
      try {
        setLoading(true);
        const res = await apiService.getFunis();
        if (res.success && res.data && res.data.length > 0) {
          setFunnels(res.data);
          // Seleciona o primeiro funil por padrão
          setSelectedFunnel(res.data[0].id);
        } else {
          setError("Nenhum funil encontrado. Por favor, crie um funil primeiro.");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Erro ao carregar funis";
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    
    loadFunnels();
  }, []);
  
  // Carregar configurações ao selecionar um funil
  useEffect(() => {
    if (selectedFunnel) {
      loadConfiguracoesEtapas();
      loadConfiguracoesValor();
    }
  }, [selectedFunnel]);
  
  // Função para carregar configurações de etapas
  const loadConfiguracoesEtapas = async () => {
    if (!selectedFunnel) return;
    
    try {
      setLoadingEtapas(true);
      const res = await apiService.getConfiguracoesEtapasOrcamento(selectedFunnel);
      
      if (res.success) {
        setEtapasConfig(res.data);
      } else {
        setEtapasConfig([]);
      }
    } catch (err) {
      console.error("Erro ao carregar configurações de etapas:", err);
      toast.error("Erro ao carregar configurações de etapas");
    } finally {
      setLoadingEtapas(false);
    }
  };
  
  // Função para carregar configuração de valor da oportunidade
  const loadConfiguracoesValor = async () => {
    if (!selectedFunnel) return;
    
    try {
      setLoadingValor(true);
      const res = await apiService.getConfiguracaoValorOportunidadeByFunil(selectedFunnel);
      
      if (res.success) {
        setValorConfig(res.data);
        setEtapaMudancaValor(res.data.id_etapa_mudanca_valor);
      } else {
        setValorConfig(null);
        setEtapaMudancaValor(null);
      }
    } catch (err) {
      console.error("Erro ao carregar configuração de valor:", err);
      // Não exibir toast aqui, pois pode não existir configuração
      setValorConfig(null);
    } finally {
      setLoadingValor(false);
    }
  };
  
  // Obter etapas do funil selecionado
  const getEtapas = () => {
    if (!selectedFunnel) return [];
    const funnel = funnels.find(f => f.id === selectedFunnel);
    return funnel?.etapas || [];
  };
  
  // Função para atualizar configuração de etapa
  const handleUpdateEtapaConfig = async (etapaId: number, field: string, value: boolean) => {
    try {
      const config = etapasConfig.find(c => c.id_etapa === etapaId);
      
      if (config) {
        // Atualizar configuração existente
        await apiService.updateConfiguracaoEtapaOrcamento(config.id, {
          [field]: value
        });
      } else {
        // Criar nova configuração
        await apiService.createConfiguracaoEtapaOrcamento({
          id_funil: selectedFunnel!,
          id_etapa: etapaId,
          permite_editar_fornecedor: field === 'permite_editar_fornecedor' ? value : false,
          permite_editar_valor_orcado: field === 'permite_editar_valor_orcado' ? value : false,
          permite_editar_valor_proposta: field === 'permite_editar_valor_proposta' ? value : false
        });
      }
      
      toast.success("Configuração atualizada com sucesso");
      loadConfiguracoesEtapas();
    } catch (err) {
      console.error("Erro ao atualizar configuração:", err);
      toast.error("Erro ao atualizar configuração");
    }
  };
  
  // Função para salvar configuração de valor
  const handleSaveValorConfig = async () => {
    if (!selectedFunnel || !etapaMudancaValor) {
      toast.error("Selecione um funil e uma etapa");
      return;
    }
    
    try {
      if (valorConfig) {
        // Atualizar configuração existente
        await apiService.updateConfiguracaoValorOportunidade(valorConfig.id, {
          id_etapa_mudanca_valor: etapaMudancaValor
        });
      } else {
        // Criar nova configuração
        await apiService.createConfiguracaoValorOportunidade({
          id_funil: selectedFunnel,
          id_etapa_mudanca_valor: etapaMudancaValor
        });
      }
      
      toast.success("Configuração de valor salva com sucesso");
      loadConfiguracoesValor();
    } catch (err) {
      console.error("Erro ao salvar configuração de valor:", err);
      toast.error("Erro ao salvar configuração de valor");
    }
  };
  
  // Verificar se uma etapa tem configuração e se um campo está habilitado
  const isCampoHabilitado = (etapaId: number, campo: string): boolean => {
    const config = etapasConfig.find(c => c.id_etapa === etapaId);
    return config ? config[campo] : false;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Page Header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-sm-8">
                  <h4 className="page-title">Configurações de Orçamento</h4>
                </div>
                <div className="col-sm-4 text-sm-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            
            {/* Settings Menu */}
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom">
                  <li className="nav-item me-3">
                    <Link 
                      to="#" 
                      className="nav-link px-0 active"
                      onClick={() => setActiveTab('etapas')}
                    >
                      <i className="ti ti-settings-cog" /> Configurações de Orçamento
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            {/* /Settings Menu */}
            
            <div className="row">
              <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                {/* Settings Sidebar */}
                <div className="card">
                  <div className="card-body">
                    <div className="settings-sidebar">
                      <h5 className="fw-semibold mb-3">Configurações de Orçamento</h5>
                      <div className="list-group list-group-flush settings-sidebar">
                        <Link 
                          to="#" 
                          className={`fw-medium ${activeTab === 'etapas' ? 'active' : ''}`}
                          onClick={() => setActiveTab('etapas')}
                        >
                          Configuração de Etapas
                        </Link>
                        <Link 
                          to="#" 
                          className={`fw-medium ${activeTab === 'valor' ? 'active' : ''}`}
                          onClick={() => setActiveTab('valor')}
                        >
                          Configuração de Valores
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Settings Sidebar */}
              </div>
              
              <div className="col-xl-9 col-lg-12">
                {/* Seleção de Funil */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h4 className="fw-semibold mb-3">Selecione o Funil</h4>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Funil <span className="text-danger">*</span>
                          </label>
                          <Select
                            className="select2"
                            options={funnels.map(f => ({
                              value: f.id,
                              label: f.nome
                            }))}
                            value={selectedFunnel ? {
                              value: selectedFunnel,
                              label: funnels.find(f => f.id === selectedFunnel)?.nome || ''
                            } : null}
                            onChange={(option) => setSelectedFunnel(option?.value as number)}
                            placeholder="Selecione o funil"
                            isDisabled={loading}
                            classNamePrefix="react-select"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Conteúdo da Aba */}
                {activeTab === 'etapas' && (
                  <div className="card">
                    <div className="card-body">
                      <h4 className="fw-semibold mb-3">Configurações de Etapas</h4>
                      <p className="text-muted mb-4">
                        Defina quais campos podem ser editados em cada etapa do funil. 
                        Isso controla o que os usuários podem modificar durante o processo de orçamento.
                      </p>
                      
                      {loadingEtapas ? (
                        <div className="text-center py-3">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                          </div>
                        </div>
                      ) : getEtapas().length === 0 ? (
                        <div className="alert alert-info mb-0">
                          Este funil não possui etapas configuradas. Por favor, adicione etapas ao funil primeiro.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>Etapa</th>
                                <th>Editar Fornecedor</th>
                                <th>Editar Valor Orçado</th>
                                <th>Editar Valor Proposta</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getEtapas().map((etapa) => (
                                <tr key={etapa.id}>
                                  <td>{etapa.nome}</td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isCampoHabilitado(etapa.id!, 'permite_editar_fornecedor')}
                                        onChange={(e) => handleUpdateEtapaConfig(
                                          etapa.id!, 
                                          'permite_editar_fornecedor', 
                                          e.target.checked
                                        )}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isCampoHabilitado(etapa.id!, 'permite_editar_valor_orcado')}
                                        onChange={(e) => handleUpdateEtapaConfig(
                                          etapa.id!, 
                                          'permite_editar_valor_orcado', 
                                          e.target.checked
                                        )}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isCampoHabilitado(etapa.id!, 'permite_editar_valor_proposta')}
                                        onChange={(e) => handleUpdateEtapaConfig(
                                          etapa.id!, 
                                          'permite_editar_valor_proposta', 
                                          e.target.checked
                                        )}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {activeTab === 'valor' && (
                  <div className="card">
                    <div className="card-body">
                      <h4 className="fw-semibold mb-3">Configuração de Valores</h4>
                      <p className="text-muted mb-4">
                        Configure em qual etapa do funil o valor da oportunidade deve mudar do valor de referência 
                        para o valor de proposta.
                      </p>
                      
                      {loadingValor ? (
                        <div className="text-center py-3">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                          </div>
                        </div>
                      ) : getEtapas().length === 0 ? (
                        <div className="alert alert-info mb-0">
                          Este funil não possui etapas configuradas. Por favor, adicione etapas ao funil primeiro.
                        </div>
                      ) : (
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveValorConfig(); }}>
                          <div className="row">
                            <div className="col-md-8">
                              <div className="mb-3">
                                <label className="form-label">
                                  Etapa de mudança de valor <span className="text-danger">*</span>
                                </label>
                                <p className="text-muted small">
                                  Quando a oportunidade avançar para esta etapa, o valor exibido mudará automaticamente 
                                  do valor de referência para o valor de proposta.
                                </p>
                                <Select
                                  className="select2"
                                  options={getEtapas().map(e => ({
                                    value: e.id!,
                                    label: e.nome
                                  }))}
                                  value={etapaMudancaValor ? {
                                    value: etapaMudancaValor,
                                    label: getEtapas().find(e => e.id === etapaMudancaValor)?.nome || ''
                                  } : null}
                                  onChange={(option) => setEtapaMudancaValor(option?.value as number)}
                                  placeholder="Selecione a etapa"
                                  classNamePrefix="react-select"
                                />
                              </div>
                              
                              <div className="mt-4">
                                <button
                                  type="submit"
                                  className="btn btn-primary"
                                  disabled={!etapaMudancaValor}
                                >
                                  Salvar Configuração
                                </button>
                              </div>
                            </div>
                            
                            <div className="col-md-4">
                              <div className="alert alert-info">
                                <h6 className="alert-heading fw-bold"><i className="ti ti-info-circle me-2"></i>Importante</h6>
                                <p className="mb-0">
                                  Esta configuração afeta todas as oportunidades deste funil. 
                                  Ao avançar para a etapa selecionada, o valor da oportunidade 
                                  será automaticamente alterado para o valor de proposta.
                                </p>
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesOrcamento;